import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/supabase';
import { SignedImage } from '../../hooks/useSignedUrl';
import {
  LoanOfficer,
  LOProgressSummary,
  LOCategoryProgress,
  A2PRegistration,
  StepStatus,
  statusColors,
  statusLabels,
  a2pStatusColors,
  a2pStatusLabels,
  getOnboardingStatus,
  formatRelativeDate,
  formatPhone,
} from '../../types';
import { ArrowPathIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

const ITEMS_PER_PAGE = 10;

type LoanOfficerRow = LoanOfficer & {
  progress_summary?: LOProgressSummary;
  a2p_registration?: A2PRegistration | null;
  a2p_business_name?: string | null;
};

const statusFilterOptions: { label: string; value: 'all' | StepStatus }[] = [
  { label: 'All', value: 'all' },
  { label: 'Not Started', value: 'not_started' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Blocked', value: 'blocked' },
  { label: 'Completed', value: 'completed' },
];

type SortColumn = 'name' | 'contact' | 'progress' | 'a2p' | 'updated';
type SortDirection = 'asc' | 'desc';


const LoanOfficers: React.FC = () => {
  const navigate = useNavigate();
  const { isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const [loanOfficers, setLoanOfficers] = useState<LoanOfficerRow[]>([]);
  const [categoryProgress, setCategoryProgress] = useState<Map<string, LOCategoryProgress[]>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StepStatus>('all');
  const [sortColumn, setSortColumn] = useState<SortColumn>('updated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const loadLoanOfficers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: losData, error: losError } = await supabase
        .from('profiles')
        .select(
          'id, location_id, full_name, first_name, last_name, phone, cell_number, email, nmls, nmls_number, title, role, headshot, user_role, created_at'
        )
        .eq('user_role', 'user')
        .order('created_at', { ascending: false });

      if (losError) throw losError;

      const [progressData, a2pData, categoryData, businessData] = await Promise.all([
        api.getAllLOsWithProgress(),
        api.getAllA2PRegistrations().catch(() => []),
        api.getAllCategoryProgress().catch(() => []),
        api.getAllA2PBusinessRegistrations().catch(() => []),
      ]);

      // Group category progress by loan_officer_id
      const categoryMap = new Map<string, LOCategoryProgress[]>();
      (categoryData || []).forEach((cp: LOCategoryProgress) => {
        const loId = String(cp.loan_officer_id);
        if (!categoryMap.has(loId)) {
          categoryMap.set(loId, []);
        }
        categoryMap.get(loId)!.push(cp);
      });
      setCategoryProgress(categoryMap);

      // Create a map of business names by loan_officer_id
      const businessMap = new Map<string, string>();
      (businessData || []).forEach((b: { loan_officer_id: string; business_name: string }) => {
        businessMap.set(String(b.loan_officer_id), b.business_name);
      });

      const merged: LoanOfficerRow[] = (losData || []).map((lo) => {
        const id = String(lo.id);
        return {
          ...lo,
          id,
          progress_summary: progressData?.find((p) => String(p.loan_officer_id) === id),
          a2p_registration:
            (a2pData as A2PRegistration[]).find((entry) => String(entry.loan_officer_id) === id) || null,
          a2p_business_name: businessMap.get(id) || null,
        };
      });

      setLoanOfficers(merged);
    } catch (err: any) {
      // Error handled
      setError(err.message || 'Unable to load loan officers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLoanOfficers();

    let debounceTimer: ReturnType<typeof setTimeout>;
    const channel = supabase
      .channel('roster-progress')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loan_officer_progress',
        },
        () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => loadLoanOfficers(), 500);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sortColumn, sortDirection]);

  const filteredLoanOfficers = useMemo(() => {
    let list = [...loanOfficers];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter((lo) => {
        const fullName = `${lo.first_name ?? ''} ${lo.last_name ?? ''}`.toLowerCase();
        return (
          fullName.includes(query) ||
          lo.email?.toLowerCase().includes(query) ||
          lo.phone?.includes(query) ||
          lo.nmls?.toString().includes(query)
        );
      });
    }

    if (statusFilter === 'all') {
      // Hide completed LOs from "All" view - they only show in "Completed" filter
      list = list.filter((lo) => getOnboardingStatus(lo.progress_summary) !== 'completed');
    } else {
      list = list.filter((lo) => getOnboardingStatus(lo.progress_summary) === statusFilter);
    }

    list.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name': {
          const nameA = `${a.first_name ?? ''} ${a.last_name ?? ''}`.toLowerCase();
          const nameB = `${b.first_name ?? ''} ${b.last_name ?? ''}`.toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'contact': {
          const emailA = (a.email ?? '').toLowerCase();
          const emailB = (b.email ?? '').toLowerCase();
          comparison = emailA.localeCompare(emailB);
          break;
        }
        case 'progress': {
          const progressA = a.progress_summary?.completion_percentage ?? 0;
          const progressB = b.progress_summary?.completion_percentage ?? 0;
          comparison = progressA - progressB;
          break;
        }
        case 'a2p': {
          // Sort by brand status first, then campaign
          const statusOrder = { approved: 3, pending: 2, failed: 1, not_started: 0 };
          const a2pA = statusOrder[a.a2p_registration?.brand_status ?? 'not_started'];
          const a2pB = statusOrder[b.a2p_registration?.brand_status ?? 'not_started'];
          comparison = a2pA - a2pB;
          break;
        }
        case 'updated':
        default: {
          const dateA = new Date(a.progress_summary?.latest_completion_date || a.created_at).getTime();
          const dateB = new Date(b.progress_summary?.latest_completion_date || b.created_at).getTime();
          comparison = dateA - dateB;
          break;
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return list;
  }, [loanOfficers, searchQuery, sortColumn, sortDirection, statusFilter]);

  const totalPages = Math.ceil(filteredLoanOfficers.length / ITEMS_PER_PAGE) || 1;
  const paginatedLoanOfficers = filteredLoanOfficers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const metrics = useMemo(() => {
    const totals = {
      total: loanOfficers.length,
      inProgress: 0,
      blocked: 0,
      completed: 0,
      brandApproved: 0,
      campaignApproved: 0,
    };

    loanOfficers.forEach((lo) => {
      const status = getOnboardingStatus(lo.progress_summary);
      if (status === 'completed') totals.completed += 1;
      else if (status === 'blocked') totals.blocked += 1;
      else if (status === 'in_progress') totals.inProgress += 1;

      if (lo.a2p_registration?.brand_status === 'approved') totals.brandApproved += 1;
      if (lo.a2p_registration?.campaign_status === 'approved') totals.campaignApproved += 1;
    });

    return totals;
  }, [loanOfficers]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortableHeader: React.FC<{ column: SortColumn; label: string; className?: string }> = ({ column, label, className = '' }) => {
    const isActive = sortColumn === column;
    return (
      <th
        className={`px-6 py-4 text-left cursor-pointer hover:bg-slate-100 transition-colors select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive ? (
            <ChevronDownIcon
              className={`h-3.5 w-3.5 text-lumin-primary transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
            />
          ) : (
            <ChevronUpDownIcon className="h-3.5 w-3.5 text-slate-300" />
          )}
        </div>
      </th>
    );
  };

  const renderStatusBadge = (status: StepStatus) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-2xl text-xs font-semibold ${statusColors[status]}`}>
      {statusLabels[status]}
    </span>
  );

  const renderA2PBadge = (status: A2PRegistration['brand_status']) => (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-2xl text-[11px] font-semibold border ${a2pStatusColors[status]}`}
    >
      {a2pStatusLabels[status]}
    </span>
  );

  return (
    <AdminShell
      title="Onboarding"
      description="Command view of every onboarding track, A2P status, and admin signal."
      nav={navigation}
      actions={
        <button
          onClick={loadLoanOfficers}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-2xl border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20 disabled:opacity-70"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      }
    >
      <div className="space-y-8">
        {error && (
          <div className="rounded-3xl border border-rose-100 bg-rose-50 px-5 py-4 text-sm text-rose-700">
            {error}
          </div>
        )}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Active</p>
            <p className="text-3xl font-semibold text-slate-900 mt-2">{metrics.inProgress}</p>
          </div>
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Blocked</p>
            <p className="text-3xl font-semibold text-amber-600 mt-2">{metrics.blocked}</p>
          </div>
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Completed</p>
            <p className="text-3xl font-semibold text-emerald-600 mt-2">{metrics.completed}</p>
          </div>
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">A2P Ready</p>
            <p className="text-3xl font-semibold text-slate-900 mt-2">{metrics.brandApproved}</p>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-5 space-y-4 lg:flex lg:items-center lg:justify-between lg:space-y-0">
            <div className="relative max-w-md w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
                placeholder="Search name, email, or NMLS"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {statusFilterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`px-3 py-1.5 rounded-2xl text-xs font-semibold border transition ${
                    statusFilter === option.value
                      ? 'border-lumin-primary bg-[#e7f3ff] text-lumin-primary'
                      : 'border-slate-200 text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-[#f8fafc] text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                <tr>
                  <SortableHeader column="name" label="Loan Officer" />
                  <SortableHeader column="contact" label="Contact" />
                  <SortableHeader column="progress" label="Trajectory" />
                  <SortableHeader column="a2p" label="A2P" />
                  <SortableHeader column="updated" label="Updated" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedLoanOfficers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-slate-500">
                      {loading ? 'Loading loan officers...' : 'No loan officers match your filters yet.'}
                    </td>
                  </tr>
                )}

                {paginatedLoanOfficers.map((lo) => {
                  const onboardingStatus = getOnboardingStatus(lo.progress_summary);
                  const completion = lo.progress_summary?.completion_percentage ?? 0;
                  const brandStatus = lo.a2p_registration?.brand_status ?? 'not_started';
                  const campaignStatus = lo.a2p_registration?.campaign_status ?? 'not_started';
                  return (
                    <tr
                      key={lo.id}
                      onClick={() => navigate(`/admin/loan-officers/${lo.id}`)}
                      className="cursor-pointer transition hover:bg-[#f9fbff]"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <SignedImage
                            src={lo.headshot}
                            alt={`${lo.first_name} ${lo.last_name}`}
                            className="h-10 w-10 rounded-full object-cover border-2 border-slate-100"
                            fallback={
                              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-semibold text-sm border-2 border-slate-200">
                                {(lo.first_name?.charAt(0) || '') + (lo.last_name?.charAt(0) || '')}
                              </div>
                            }
                          />
                          <div className="space-y-1">
                            <p className="text-base font-semibold text-slate-900">
                              {(lo.first_name || '') + ' ' + (lo.last_name || '')}
                            </p>
                            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{lo.title || 'Loan Officer'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <p className="text-slate-900">{lo.email || '—'}</p>
                        <div className="text-xs text-slate-500">
                          {lo.cell_number && <div>C: {formatPhone(lo.cell_number)}</div>}
                          {lo.phone && <div>W: {formatPhone(lo.phone)}</div>}
                          {!lo.cell_number && !lo.phone && <span>No phone on file</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            {renderStatusBadge(onboardingStatus)}
                            <span className="text-sm font-semibold text-slate-700">{completion.toFixed(0)}%</span>
                          </div>
                          {/* Category Progress List */}
                          {(() => {
                            const categories = categoryProgress.get(lo.id) || [];
                            if (categories.length === 0) {
                              // Fallback to single bar if no category data
                              return (
                                <div>
                                  <div className="h-2 w-full rounded-full bg-slate-100">
                                    <div
                                      className="h-2 rounded-full bg-lumin-primary"
                                      style={{ width: `${completion}%` }}
                                    />
                                  </div>
                                  <p className="mt-1 text-xs text-slate-500">{completion.toFixed(0)}% complete</p>
                                </div>
                              );
                            }
                            return (
                              <div className="space-y-1">
                                {categories.map((cat) => {
                                  const isComplete = cat.completion_percentage === 100;
                                  const hasBlocked = cat.blocked_steps > 0;
                                  return (
                                    <div key={cat.category} className="flex items-center gap-2">
                                      <span className="text-[10px] text-slate-500 w-20 truncate" title={cat.category_name}>
                                        {cat.category_name}
                                      </span>
                                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                        <div
                                          className="h-full rounded-full transition-all"
                                          style={{
                                            width: `${cat.completion_percentage}%`,
                                            backgroundColor: hasBlocked ? '#f43f5e' : (isComplete ? '#10b981' : cat.category_color),
                                          }}
                                        />
                                      </div>
                                      <span className={`text-[10px] w-8 text-right ${isComplete ? 'text-emerald-600 font-medium' : hasBlocked ? 'text-rose-600' : 'text-slate-400'}`}>
                                        {cat.completion_percentage}%
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 space-y-2">
                        {lo.a2p_business_name && (
                          <p className="text-xs font-medium text-slate-700 truncate max-w-[180px]" title={lo.a2p_business_name}>
                            {lo.a2p_business_name}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Brand</span>
                            {renderA2PBadge(brandStatus)}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">Campaign</span>
                            {renderA2PBadge(campaignStatus)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {formatRelativeDate(lo.progress_summary?.latest_completion_date || lo.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredLoanOfficers.length > ITEMS_PER_PAGE && (
            <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredLoanOfficers.length)} of{' '}
                {filteredLoanOfficers.length} loan officers
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-white border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  Previous
                </button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-white border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminShell>
  );
};

export default LoanOfficers;
